import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import 'rsuite/dist/rsuite.min.css';
import './FiltersPanel.css';
import { DateRangePicker, Radio, RadioGroup } from 'rsuite';
import ClearIcon from '@mui/icons-material/Clear';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PublicIcon from '@mui/icons-material/Public';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  createTheme,
  Button,
  Tooltip,
  Typography,
  ThemeProvider,
  Box,
  Paper,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { TimezoneContext } from '../TimezoneContext/TimezoneContext';
import { useSnackbarNotification } from '../../Hooks/useSnackbarNotification';

const FiltersComponent = ({
  activeTheme,
  dropdowns,
  dateRangePicker,
  dateTypeSelector,
  uploadSourceSelector,
  viewModeSelector,
  requestTypeFilter,
  statusFilter,
  onSaveSearch,
  storageKey,
  showSaveButton = true,
}) => {
  const { timezone, userTimezone, handleTimezoneChange } = useContext(TimezoneContext);
  const [hasSavedData, setHasSavedData] = useState(false);
  const [isSavingInProgress, setIsSavingInProgress] = useState(false);
  const { showSnackbar } = useSnackbarNotification();

  const checkSavedData = () => {
    if (showSaveButton && storageKey) {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          const hasData = Object.values(parsed).some(
            (value) => Array.isArray(value) && value.length > 0
          );
          setHasSavedData(hasData);
        } else {
          setHasSavedData(false);
        }
      } catch (error) {
        setHasSavedData(false);
      }
    }
  };

  useEffect(() => {
    if (!showSaveButton) {
      return undefined;
    }

    checkSavedData();
    const handleStorageChange = (e) => {
      if (e.key === storageKey) {
        checkSavedData();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    const handleCustomStorageChange = () => {
      checkSavedData();
    };
    window.addEventListener('localStorageChange', handleCustomStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleCustomStorageChange);
    };
  }, [showSaveButton, storageKey]);

  const handleSaveClick = async () => {
    if (!onSaveSearch) return;
    setIsSavingInProgress(true);
    try {
      const prevSaved = hasSavedData;
      const result = await Promise.resolve(onSaveSearch());
      await new Promise((r) => setTimeout(r, 80));
      checkSavedData();
      if (result && result.message) {
        showSnackbar({ message: result.message, severity: result.saved ? 'success' : 'info' });
      } else {
        showSnackbar({
          message: prevSaved ? 'Filters unsaved for this page.' : 'Filters saved — they will reload next visit.',
          severity: prevSaved ? 'info' : 'success',
        });
      }
    } finally {
      setIsSavingInProgress(false);
    }
  };

  const themeMode = activeTheme?.palette?.mode || 'light';
  const theme = useMemo(() => createTheme({ palette: { mode: themeMode } }), [themeMode]);

  const dateRangeValue = useMemo(() => {
    if (
      !dateRangePicker?.filters?.start_datetime ||
      !dateRangePicker?.filters?.end_datetime
    ) {
      return undefined;
    }

    return [
      dateRangePicker.filters.start_datetime.toDate(),
      dateRangePicker.filters.end_datetime.toDate(),
    ];
  }, [
    dateRangePicker?.filters?.start_datetime?.valueOf?.(),
    dateRangePicker?.filters?.end_datetime?.valueOf?.(),
  ]);

  const handleDateRangeChange = useCallback((dateRange) => {
    if (!dateRange || dateRange.length === 0) {
      dateRangePicker?.handleDateRangeChange([null, null]);
      return;
    }

    dateRangePicker?.handleDateRangeChange(dateRange);
  }, [dateRangePicker]);

  const clearFilters = useCallback(() => {
    window.dispatchEvent(new Event('clearAllFilters'));
  }, []);

  const DateRangePickerComponent = () => (
    <Box className="date-range-container">
      <Box className="icon-title-container">
        <CalendarTodayIcon className="icon" />
        <Typography variant="subtitle2" className="title">
          Date Range
        </Typography>
      </Box>
      <DateRangePicker
        format="yyyy-MM-dd HH:mm:ss"
        className="date-range-picker"
        placeholder="Select date range"
        cleanable={true}
        required={false}
        value={dateRangeValue}
        onChange={handleDateRangeChange}
      />
    </Box>
  );


  const DateTypeSelector = () => {
    if (!dateTypeSelector) return null;

    return (
      <Box className="date-type-container">
        <Box className="icon-title-container" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarTodayIcon className="icon" />
            <Typography variant="subtitle2" className="title">
              Date Type
            </Typography>
          </Box>
        </Box>

        <RadioGroup
          name="dateType"
          value={dateTypeSelector.dateType || ''}
          onChange={(value) => dateTypeSelector.handleDateTypeChange(value)}
          className="radio-group"
          inline
        >
          <Radio value="datetime">Created on Device</Radio>
          <Radio value="upload">Uploaded to Cloud</Radio>
        </RadioGroup>
        
      </Box>
    );
  };

  const UploadSourceSelector = () => {
    if (!uploadSourceSelector) return null;

    return (
      <Box className="date-type-container">
        <Box className="icon-title-container" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudUploadIcon className="icon" />
          <Typography variant="subtitle2" className="title">
            Upload Category
          </Typography>
        </Box>
        <RadioGroup
          name="uploadSource"
          value={uploadSourceSelector.uploadSource || ''}
          onChange={(value) => uploadSourceSelector.handleUploadSourceChange(value)}
          className="radio-group"
          inline
        >
          <Radio value="">All Files</Radio>
          <Radio value="filesapi">FilesAPI Upload</Radio>
        </RadioGroup>
      </Box>
    );
  };

  const TimezoneSelector = () => (
    <Box className="timezone-container">
      <Box className="icon-title-container">
        <PublicIcon className="icon" />
        <Typography variant="subtitle2" className="title">
          Time Zone
        </Typography>
      </Box>
      <RadioGroup
        name="timezone"
        value={timezone}
        onChange={(value) => handleTimezoneChange(value)}
        className="radio-group"
        inline
      >
        <Radio value="local">
          <Box className="label-container">
            <Typography variant="body2">Local</Typography>
            <Chip label={userTimezone} size="small" className="chip" />
          </Box>
        </Radio>
        <Radio value="utc">
          <Typography variant="body2">UTC</Typography>
        </Radio>
      </RadioGroup>
    </Box>
  );

  const ViewModeSelector = () => {
    if (!viewModeSelector) return null;

    const options = viewModeSelector.options || [
      { value: 'devices', label: 'Devices' },
      { value: 'pending', label: 'Requests' },
    ];

    return (
      <Box className="view-mode-container">
        <Box className="icon-title-container">
          <ViewModuleIcon className="icon" />
          <Typography variant="subtitle2" className="title">
            View Mode
          </Typography>
        </Box>
        <RadioGroup
          name="viewMode"
          value={viewModeSelector.viewMode || ''}
          onChange={(value) => {
            if (value !== null && viewModeSelector.handleViewModeChange) {
              viewModeSelector.handleViewModeChange(value);
            }
          }}
          className="radio-group"
          inline
        >
          {options.map((opt) => (
            <Radio key={opt.value} value={opt.value}>
              <Typography variant="body2">{opt.label}</Typography>
            </Radio>
          ))}
        </RadioGroup>
      </Box>
    );
  };

  const RequestTypeFilterComponent = () => {
    if (!requestTypeFilter || viewModeSelector?.viewMode !== 'pending') return null;

    return (
      <Box className="request-type-filter-container">
        <Box className="icon-title-container">
          <FilterListIcon className="icon" />
          <Typography variant="subtitle2" className="title">
            Request Type
          </Typography>
        </Box>
        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
          <InputLabel>Filter by Type</InputLabel>
          <Select
            value={requestTypeFilter.value || ''}
            label="Filter by Type"
            onChange={(e) => requestTypeFilter.onChange(e.target.value)}
          >
            {requestTypeFilter.options?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  const StatusFilterComponent = () => {
    if (!statusFilter || viewModeSelector?.viewMode !== 'pending') return null;

    return (
      <Box className="status-filter-container">
        <Box className="icon-title-container">
          <FilterListIcon className="icon" />
          <Typography variant="subtitle2" className="title">
            Status
          </Typography>
        </Box>
        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
          <InputLabel>Filter by Status</InputLabel>
          <Select
            value={statusFilter.value || ''}
            label="Filter by Status"
            onChange={(e) => statusFilter.onChange(e.target.value)}
          >
            {statusFilter.options?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <Paper className="filters-paper">
        <ViewModeSelector />
        <TimezoneSelector />
        <RequestTypeFilterComponent />
        <StatusFilterComponent />
        <DateTypeSelector />
        <UploadSourceSelector />
        {dateRangePicker && <DateRangePickerComponent />}
        <Box className="dropdowns-container">{dropdowns}</Box>
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          {onSaveSearch && showSaveButton && (
            <Tooltip title={hasSavedData ? 'Unsave filters' : 'Save current filters'} arrow>
              <Button
                variant="contained"
                color={hasSavedData ? 'success' : 'primary'}
                onClick={handleSaveClick}
                disabled={isSavingInProgress}
                sx={{ flex: 1, minWidth: 'auto' }}
                aria-label={hasSavedData ? 'Unsave filters' : 'Save filters'}
              >
                {isSavingInProgress
                  ? <CircularProgress size={18} color="inherit" />
                  : hasSavedData ? <BookmarkIcon /> : <BookmarkBorderIcon />}
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Clear All Filters" arrow>
            <Button
              variant="contained"
              color="primary"
              onClick={clearFilters}
              sx={{ flex: 1, minWidth: 'auto' }}
              aria-label="Clear All Filters"
            >
              <ClearIcon />
            </Button>
          </Tooltip>
        </Box>
      </Paper>

    </ThemeProvider>
  );
};

export default React.memo(FiltersComponent);